import { ArrowRight, Heart } from '@phosphor-icons/react';
import {
  Avatar,
  Button,
  Heading,
  Icon,
  PointsPill,
  ProgressRing,
  Surface,
  Text,
  buttonStyles,
  cn,
  personInk,
} from '@shh/ui';
import { useState } from 'react';
import { ProgressBar } from 'react-aria-components';
import { Link, useNavigate } from 'react-router';
import { useLens } from '../app/lens';
import { Approvals } from '../components/Approvals';
import { KudosSheet, RewardShop } from '../components/Rewards';
import { goalRewardOf, kids, memberById, unitFor, type M } from '../data/derive';
import { useFamily } from '../data/family';
import { numberFormat } from '../lib/locale';
import { starsText } from '../i18n/stars';

const numFmt = numberFormat();

/** Stars: the family goal, everybody's progress, rewards – and for parents approvals and kudos. */
export function StarsPage() {
  const f = useFamily();
  const lens = useLens();
  const navigate = useNavigate();
  const [kudosFor, setKudosFor] = useState<M | null>(null);
  const shopFor = lens.member && lens.member.role !== 'parent' ? lens.member : undefined;
  // a child chosen: only their stars (Mike, 24.10.: "sehe dann immer nur die Angaben, nicht von allen")
  const children = kids(f).filter((k) => !shopFor || k.id === shopFor.id);

  return (
    <>
      {f.goal && (
        <Surface className="flex flex-col gap-gap-sm">
          <ProgressBar
            value={Math.min(f.goal.progress, f.goal.target)}
            maxValue={f.goal.target}
            aria-label={starsText.goalLabel(f.goal.title)}
            valueLabel={starsText.goalValue(
              numFmt.format(f.goal.progress),
              numFmt.format(f.goal.target),
            )}
            className="flex flex-col gap-gap-sm"
          >
            {({ percentage }) => (
              <>
                <span className="text-xs font-bold tracking-wider text-text-muted uppercase">
                  {starsText.goal}
                </span>
                <div className="flex flex-wrap items-baseline justify-between gap-gap-sm">
                  <span className="font-display text-2xl font-black">{f.goal!.title}</span>
                  <span className="font-bold tabular-nums">
                    {starsText.goalStars(
                      numFmt.format(f.goal!.progress),
                      numFmt.format(f.goal!.target),
                    )}
                  </span>
                </div>
                <span className="h-5 overflow-hidden rounded-chip bg-reward-points-surface">
                  <span
                    className="block h-full rounded-chip bg-reward-points transition-all duration-(--shh-motion-duration-slow) ease-bounce"
                    style={{ width: `${percentage ?? 0}%` }}
                  />
                </span>
              </>
            )}
          </ProgressBar>
        </Surface>
      )}

      {lens.canParent && <Approvals />}

      <section aria-labelledby="everyone" className="flex flex-col gap-gap-md">
        <Heading level={2} size="md" id="everyone">
          {starsText.kids}
        </Heading>
        <ul className="grid grid-cols-1 gap-gap-md md:grid-cols-2 xl:grid-cols-3">
          {children.map((k) => {
            const goal = goalRewardOf(f, k);
            const unit = unitFor(k);
            return (
              <li key={k.id}>
                <Surface
                  className={cn(
                    'flex h-full flex-col gap-gap-md',
                    lens.id === k.id && 'ring-3 ring-accent-secondary',
                  )}
                >
                  <div className="flex items-center gap-gap-md">
                    <Avatar name={k.name} color={k.color} emoji={k.emoji} size="md" decorative />
                    <div className="flex flex-1 flex-col">
                      <span className={cn('font-display text-lg font-black', personInk[k.color])}>
                        {k.name}
                      </span>
                      <PointsPill points={k.balance} unit={unit} />
                    </div>
                    {goal && (
                      <ProgressRing
                        value={k.balance}
                        max={goal.cost}
                        label={starsText.progressTo(k.name, goal.title)}
                        valueLabel={starsText.ofCost(String(k.balance), String(goal.cost))}
                        className="size-avatar-md"
                      >
                        <span className="text-xs">
                          {Math.min(100, Math.round((k.balance / goal.cost) * 100))}%
                        </span>
                      </ProgressRing>
                    )}
                  </div>
                  {goal && (
                    <Text size="sm" tone="secondary">
                      {starsText.target} <strong className="text-text-primary">{goal.title}</strong>
                    </Text>
                  )}
                  <div className="mt-auto flex flex-wrap gap-gap-sm">
                    {lens.canParent && (
                      <Button icon={Heart} onPress={() => setKudosFor(k)}>
                        {starsText.moment}
                      </Button>
                    )}
                    <Link to={`/person/${k.id}`} className={buttonStyles({ variant: 'secondary' })}>
                      {starsText.view(k.name)} <Icon icon={ArrowRight} size="sm" weight="bold" />
                    </Link>
                  </div>
                </Surface>
              </li>
            );
          })}
        </ul>
      </section>

      {shopFor ? (
        <section aria-labelledby="shop" className="flex flex-col gap-gap-md">
          <Heading level={2} size="md" id="shop">
            {starsText.rewardsFor(shopFor.name)}
          </Heading>
          <RewardShop
            member={shopFor}
            unit={unitFor(shopFor)}
            onEarnMore={() => navigate('/aufgaben')}
          />
        </section>
      ) : (
        !lens.isParent &&
        children.length > 0 && (
          <Surface tone="sunken">
            <Text>{starsText.tapName}</Text>
          </Surface>
        )
      )}

      {f.kudos.length > 0 && (
        <section aria-labelledby="moments" className="flex flex-col gap-gap-md">
          <Heading level={2} size="md" id="moments">
            {starsText.moments}
          </Heading>
          <ul className="grid grid-cols-1 gap-gap-sm md:grid-cols-2">
            {f.kudos.slice(0, 8).map((k) => (
              <li key={k.id}>
                <Surface padding="sm" className="flex items-center gap-gap-md">
                  <Icon icon={Heart} weight="fill" className="text-reward-kudos" />
                  <span className="flex-1">
                    <strong>{memberById(f, k.memberId)?.name}</strong> {k.text}
                  </span>
                  <PointsPill points={k.points} signed unit={starsText.stars} />
                </Surface>
              </li>
            ))}
          </ul>
        </section>
      )}

      <KudosSheet member={kudosFor} onClose={() => setKudosFor(null)} />
    </>
  );
}
